import React, { useState, useEffect, useCallback } from 'react';
import api from "../../services/api";
import Datepicker from "react-tailwindcss-datepicker";
import Select from "react-select";
import * as XLSX from "xlsx";
import { BrandsTypes } from '../../utils/constants';
import ScrollContainer from 'react-indiana-drag-scroll'

const formatDate = (date) => new Date(date).toISOString()?.split('T')[0];

const SalesWiseInteractions = () => {
    const today = formatDate(new Date());
    const [value, setValue] = useState({ startDate: today, endDate: today });
    const [selectedUserType, setSelectedUserType] = useState({ value: 1, label: "National" });
    const [results, setResults] = useState({ data: [], interactions: [] });

    const handleValueChange = (newValue) => setValue(newValue);

    const getData = useCallback(async () => {
        if (!selectedUserType || !value.startDate || !value.endDate) {
            alert("Please select a brand type and date range.");
            return;
        }

        try {
            const res = await api({
                url: "/effort_analysis/get_interactions",
                method: "POST",
                responseType: "json",
                data: {
                    startDate: value.startDate,
                    endDate: value.endDate,
                    new_user_type: selectedUserType.value,
                },
            });
            setResults(res.data || { data: [], interactions: [] });
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    }, [selectedUserType, value]);

    useEffect(() => {
        getData();
    }, [getData]);

    const calculatePersonStats = (person) => {
        const personInteractions = results.interactions.filter(
            (int) => int.sales_person_id === person.sales_person_id
        );

        const direct = personInteractions
            .filter((int) => int.type <= 2)
            .reduce((acc, curr) => acc + parseInt(curr.interaction_count), 0);

        const indirect = personInteractions
            .filter((int) => int.type > 2)
            .reduce((acc, curr) => acc + parseInt(curr.interaction_count), 0);

        return {
            direct,
            indirect,
            total: direct + indirect
        };
    };

    const handleExport = () => {
        const headers = [["Sales Person", "Known", "Unknown", "Missed", "Total", "Direct", "Indirect"]];
        const rows = results.data.map((person) => {
            const { direct, indirect, total } = calculatePersonStats(person);

            return [
                person.sales_person_name,
                parseInt(person.known),
                parseInt(person.unknown),
                parseInt(person.missed),
                total,
                direct,
                indirect
            ];
        });

        const totalRow = [
            "Total",
            results?.data?.reduce((sum, p) => sum + parseInt(p.known), 0) || 0,
            results?.data?.reduce((sum, p) => sum + parseInt(p.unknown), 0) || 0,
            results?.data?.reduce((sum, p) => sum + parseInt(p.missed), 0) || 0,
            results?.data?.reduce((sum, p) => sum + calculatePersonStats(p).total, 0) || 0,
            results?.data?.reduce((sum, p) => sum + calculatePersonStats(p).direct, 0) || 0,
            results?.data?.reduce((sum, p) => sum + calculatePersonStats(p).indirect, 0 || 0)
        ];

        const worksheet = XLSX.utils.aoa_to_sheet([...headers, ...rows, totalRow]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sales Impressions");

        XLSX.writeFile(workbook, `sales_impressions_${value.startDate}_to_${value.endDate}.csv`);
    };

    return (
        <div className="min-h-screen bg-gray-100 py-10 px-4">
            <div>
                <h2 className="text-2xl font-semibold text-gray-800 mb-6">Sales-wise Interactions</h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end mb-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Select Date Range</label>
                        <Datepicker
                            classNames="w-full"
                            value={value}
                            primaryColor="cyan"
                            showShortcuts
                            configs={{
                                shortcuts: {
                                    today: "today",
                                    yesterday: "yesterday",
                                    currentMonth: "currentMonth",
                                    pastMonth: "lastMonth",
                                },
                            }}
                            onChange={handleValueChange}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Select Brand Type</label>
                        <Select
                            className="react-select-container"
                            classNamePrefix="react-select"
                            options={BrandsTypes.filter(option => option.value !== 5)}
                            value={selectedUserType}
                            onChange={setSelectedUserType}
                        />
                    </div>
                    <div>
                        <button
                            onClick={handleExport}
                            className="w-full px-4 py-2 text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 rounded transition"
                        >
                            Download CSV
                        </button>
                    </div>
                </div>

                <ScrollContainer>
                    <table className="table table-bordered">
                        <thead className="bg-yellow-100 text-black font-bold">
                            <tr className="text-xs font-semibold uppercase text-left">
                                <th rowSpan="2" className="px-4 py-3 text-left">Sales Person</th>
                                <th rowSpan="2" className="px-4 py-3 text-left">Known</th>
                                <th rowSpan="2" className="px-4 py-3 text-left">Unknown</th>
                                <th rowSpan="2" className="px-4 py-3 text-left">Missed</th>
                                <th colSpan="3" className="px-4 py-3 text-center">Interactions</th>
                            </tr>
                            <tr className="text-xs font-medium uppercase text-gray-600">
                                <th className="px-4 py-2 text-left">Total</th>
                                <th className="px-4 py-2 text-left">Direct</th>
                                <th className="px-4 py-2 text-left">Indirect</th>
                            </tr>
                        </thead>
                        <tbody>
                            {results?.data?.length > 0 ? (
                                results.data.map((person) => {
                                    const { direct, indirect, total } = calculatePersonStats(person);

                                    return (
                                        <tr key={person.sales_person_id}>
                                            <td className="px-4 py-3 text-left font-medium text-black">{person.sales_person_name}</td>
                                            <td className="px-4 py-3 text-left">{parseInt(person.known)}</td>
                                            <td className="px-4 py-3 text-left">{parseInt(person.unknown)}</td>
                                            <td className="px-4 py-3 text-left">{parseInt(person.missed)}</td>
                                            <td className="px-4 py-3 text-left">{total}</td>
                                            <td className="px-4 py-3 text-left">{direct}</td>
                                            <td className="px-4 py-3 text-left">{indirect}</td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="7" className="text-center text-gray-500 py-6">
                                        No data available.
                                    </td>
                                </tr>
                            )}

                            {/* Total Row */}
                            <tr className="bg-yellow-200 font-semibold">
                                <td className="px-4 py-3 text-left">Total</td>
                                <td className="px-4 py-3 text-left">
                                    {results?.data?.reduce((sum, p) => sum + parseInt(p.known), 0) || 0}
                                </td>
                                <td className="px-4 py-3 text-left">
                                    {results?.data?.reduce((sum, p) => sum + parseInt(p.unknown), 0) || 0}
                                </td>
                                <td className="px-4 py-3 text-left">
                                    {results?.data?.reduce((sum, p) => sum + parseInt(p.missed), 0) || 0}
                                </td>
                                <td className="px-4 py-3 text-left">
                                    {results?.data?.reduce((sum, p) => sum + calculatePersonStats(p).total, 0) || 0}
                                </td>
                                <td className="px-4 py-3 text-left">
                                    {results?.data?.reduce((sum, p) => sum + calculatePersonStats(p).direct, 0) || 0}
                                </td>
                                <td className="px-4 py-3 text-left">
                                    {results?.data?.reduce((sum, p) => sum + calculatePersonStats(p).indirect, 0) || 0}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </ScrollContainer>
            </div>
        </div>
    );
};

export default SalesWiseInteractions;
